import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import type {
  CSSProperties,
  PropsWithChildren,
} from 'react';
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { alphaHex, colors } from '@/constants/colors';
import { useBodyScroll } from '@/lib/use-body-scroll';

type ModalProps = {
  show: StateType<boolean> | boolean,
  closeAction?: boolean,
  style?: CSSProperties,
  zIndex?: number,
  onBackdropClose?: () => void,
};

type InternalProps = {
  needDimShow: boolean,
  show: boolean,
  style?: CSSProperties,
  zIndex?: number,
};

const ModalDiv = styled.div<InternalProps>`
position: fixed;
z-index: ${({ zIndex }) => zIndex};
top: 0;
right: 0;
bottom: 0;
left: 0;
display: grid;
justify-content: center;
background: ${({ needDimShow }) => needDimShow ? ` ${colors.black}${alphaHex(0.4)}` : colors.transparent};
overflow-y: auto;
visibility: ${({ show }) => show ? 'visible' : 'hidden'};
`;

const Backdrop = styled.div`
position: absolute;
z-index: -10000;
width: 100%;
height: 100%;
`;

let modalPortalId = '';

const initZIndex = 10000;
let shownModalCount = 0;

const destructuringShow = (show: StateType<boolean> | boolean): [boolean, SetStateType<boolean> | null] => {
  if (typeof show === 'boolean') {
    return [show, null];
  } else {
    return show;
  }
};

const ModalPortal = ({ children }: PropsWithChildren) => {
  if (process?.env.NODE_ENV === 'test') return <>{children}</>;
  if (typeof window === 'undefined') return null;
  const container = document.getElementById(modalPortalId);
  return container && ReactDOM.createPortal(children, container);
};

const Modal = ({
  children,
  show: showState,
  closeAction = true,
  style,
  onBackdropClose,
}: PropsWithChildren<ModalProps>) => {
  const [show, setShow] = destructuringShow(showState);
  const [zIndex, setZIndex] = useState(0);
  const [needDimShow, setNeedDimShow] = useState(false);
  const showStateRef = useRef<boolean | null>(null);

  const { setBodyScroll } = useBodyScroll();

  const handleShowModal = useCallback(() => {
    if (!showStateRef.current) return;
    setNeedDimShow(shownModalCount === 0);
    shownModalCount += 1;
    setZIndex(initZIndex + shownModalCount);
    setBodyScroll(false);
  }, [setBodyScroll]);

  const handleDissmissModal = useCallback(() => {
    if (showStateRef.current) return;
    if (shownModalCount > 1) {
      shownModalCount -= 1;
    } else {
      shownModalCount = 0;
      setBodyScroll(true);
    }
  }, [setBodyScroll]);

  const handleKeyup = useCallback(({ key }: KeyboardEvent) => {
    key === 'Escape' && setShow?.(false);
  }, [setShow]);

  useEffect(() => {
    (show && closeAction ? document.addEventListener : document.removeEventListener)('keyup', handleKeyup);
  }, [closeAction, handleKeyup, show]);

  useEffect(() => {
    if (showStateRef.current === show) return;
    showStateRef.current = show;
    show ? handleShowModal() : handleDissmissModal();
  }, [handleDissmissModal, handleShowModal, show]);

  useEffect(() => () => {
    if (showStateRef.current) {
      showStateRef.current = false;
      handleDissmissModal();
    }
  }, [handleDissmissModal]);

  return (
    <ModalPortal>
      <ModalDiv
        needDimShow={needDimShow}
        show={show}
        style={style}
        zIndex={zIndex}
      >
        {show && children}
        {show && closeAction && setShow && (
          <Backdrop
            onClick={() => {
              setShow(false);
              onBackdropClose?.();
            }}
          />
        )}
      </ModalDiv>
    </ModalPortal>
  );
};

export const ModalContainer = () => {
  const router = useRouter();
  const id = useId();

  useEffect(() => {
    modalPortalId = id;
  }, [id]);

  useEffect(() => {
    shownModalCount = 0;
  }, [router.pathname]);

  return (
    <div id={id} />
  );
};

export default Modal;
